import { z } from 'zod';
import { isValidExtensionCompositeId, isValidPublisherName, } from './extension-validators';
import { PolicyKey, policyKeySchema } from './policies';
export var PolicyExceptionAction;
(function (PolicyExceptionAction) {
    PolicyExceptionAction["Allow"] = "allow";
    PolicyExceptionAction["Block"] = "block";
})(PolicyExceptionAction || (PolicyExceptionAction = {}));
export const policyExceptionActionSchema = z.enum(PolicyExceptionAction);
// ---------------------------------------------------------------------------
// Per-policy ruleObject schemas
// ---------------------------------------------------------------------------
/**
 * Checks if a value contains path traversal sequences.
 * Path traversal attempts must be rejected to prevent bypass attacks.
 * Matches actual path traversal patterns: ../, ..\, and .. at end of string.
 * Examples: ../malware, ..\evil, anthropic/.., version1..end (last is valid)
 */
export function hasPathTraversal(value) {
    return /\.\.[\\/]|\.\.$/m.test(value);
}
/**
 * Validates that a glob pattern only uses `*` at the start and/or end.
 * Rejects path traversal characters (../, ..\) to prevent bypass attacks.
 * Allowed: `*`, `foo`, `foo*`, `*foo`, `*foo*`
 * Rejected: `fo*o`, `f*o*o` (interior wildcards), `../malware`, `..\bad` (path traversal)
 */
export function isValidGlobPattern(value) {
    // Reject path traversal sequences using shared check
    if (hasPathTraversal(value))
        return false;
    if (/\*{2,}/.test(value))
        return false;
    const inner = value.replace(/^\*/, '').replace(/\*$/, '');
    return !inner.includes('*');
}
const globStringSchema = z.string().refine(isValidGlobPattern, {
    message: 'Wildcards (*) are only allowed at the start and/or end of the value. Valid examples: my-extension, my-org*, *my-publisher, *partial*',
});
export const extensionsManagementRuleSchema = z
    .object({
    name: globStringSchema.optional(),
    publisher: globStringSchema.optional(),
    version: z.string().optional(),
})
    .refine(obj => obj.name || obj.publisher, {
    message: 'At least name or publisher must be provided',
})
    .superRefine((obj, ctx) => {
    if (obj.name && !obj.name.includes('*')) {
        if (!isValidExtensionCompositeId(obj.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Name must be publisher.extension (lowercase alphanumeric and hyphens, e.g. ms.python) or a wildcard pattern',
                path: ['name'],
            });
        }
    }
    if (obj.publisher && !obj.publisher.includes('*')) {
        if (!isValidPublisherName(obj.publisher)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid publisher name (lowercase letters, digits, hyphens; optional dot-separated segments; 2-30 characters) or use a wildcard pattern',
                path: ['publisher'],
            });
        }
    }
});
export var McpServerSourceType;
(function (McpServerSourceType) {
    McpServerSourceType["Local"] = "local";
    McpServerSourceType["Remote"] = "remote";
    McpServerSourceType["Any"] = "any";
})(McpServerSourceType || (McpServerSourceType = {}));
export const mcpServerSourceTypeSchema = z.enum(McpServerSourceType);
/**
 * KIR-3904: no `version` field. MCP configs have no version, so a rule
 * constraining one can never match a real server. Already gone from the MCP
 * UI; `UNSUPPORTED_RULE_FIELDS_BY_POLICY` strips it from API-supplied rules.
 */
export const mcpServerSourcesRuleSchema = z
    .object({
    name: globStringSchema.optional(),
    publisher: globStringSchema.optional(),
    type: mcpServerSourceTypeSchema.optional(),
})
    .refine(obj => obj.name || obj.publisher, {
    message: 'At least name or publisher must be provided',
});
/**
 * Ecosystems a package rule can name. Mirrors the backend `PACKAGE_ECOSYSTEMS`
 * (OSV-canonical casing), which is where the scanner resolves a package
 * manager to an ecosystem.
 */
export const PACKAGE_RULE_ECOSYSTEMS = [
    'npm',
    'PyPI',
    'crates.io',
    'NuGet',
    'RubyGems',
    'Go',
    'Packagist',
    'Maven',
];
export const packageRuleEcosystemSchema = z.enum(PACKAGE_RULE_ECOSYSTEMS);
/**
 * Package Allow/Block rule (KIR-4443).
 *
 * Deliberately NOT glob-matched, unlike the extension and MCP rules above.
 * Package identity is `name` + `ecosystem`, and a wildcard over package names
 * is a foot-gun in a way it is not for extensions: `react*` would block
 * thousands of unrelated npm packages, and an over-broad BLOCK rule on a
 * registry that resolves transitive dependencies breaks builds far from where
 * the rule was written. Both fields are required and matched exactly.
 *
 * Version is intentionally absent from the rule: a decision about a package's
 * trustworthiness is about the package, and a version-pinned rule silently
 * stops applying the moment the agent installs a different version — the exact
 * case the rule exists to cover.
 */
export const packagesManagementRuleSchema = z
    .object({
    // `*` is rejected rather than stored: matching is exact, so a rule like
    // `react*` would be accepted and then silently match nothing.
    name: z
        .string()
        .min(1, 'Package name is required')
        .refine(name => !name.includes('*'), {
        message: 'Package rules match an exact name; wildcards (*) are not supported',
    }),
    ecosystem: packageRuleEcosystemSchema,
})
    .strict();
/**
 * The name a package rule is matched on: the registry's own identity rule, so
 * two spellings the registry installs as one project can never be told apart
 * by a rule. PyPI folds runs of `-`, `_`, `.` (PEP 503) and crates.io treats
 * `-` and `_` as the same crate; every registry here matches case-insensitively.
 *
 * Every Allow/Block comparison (backend matcher, Packages-page state, frontend
 * row actions) must go through this, or a Block on `evil-pkg` misses
 * `pip install Evil_Pkg`. `PACKAGE_IDENTITY_SQL_PATTERNS` is its SQL twin.
 *
 * The twins agree for ASCII names only. For non-ASCII input JS `trim()` strips
 * all Unicode whitespace where Postgres strips spaces, and `lower()` folds per
 * the DB collation (`'İ'.toLowerCase()` is `i̇` in JS, `i` under glibc). The
 * scanner uses this function on both sides, so a mismatch can only make the
 * Packages page's state column disagree with enforcement, never skip a block.
 */
export function packageIdentityName(name, ecosystem) {
    const lower = name.trim().toLowerCase();
    if (ecosystem === 'PyPI')
        return lower.replace(/[-_.]+/g, '-');
    if (ecosystem === 'crates.io')
        return lower.replace(/_/g, '-');
    return lower;
}
/**
 * `regexp_replace` pattern per ecosystem that {@link packageIdentityName}
 * folds to `-`, for building the same key in SQL.
 */
export const PACKAGE_IDENTITY_SQL_PATTERNS = {
    PyPI: '[-_.]+',
    'crates.io': '_',
};
/**
 * A skill name is only unique within its source: a project, the user, and a
 * plugin can each ship an unrelated `pdf-processing`. `plugin:<plugin-name>`
 * names the provider so one plugin's skill never matches another's.
 */
const SKILL_RULE_SOURCES = ['project', 'user'];
/** e.g. `plugin:code-review`. */
export const SKILL_PLUGIN_SOURCE_PREFIX = 'plugin:';
/**
 * `project`, `user`, or `plugin:<name>`. Path traversal is rejected so a
 * source can never escape the skill root when a target is rebuilt from a rule.
 */
function isValidSkillSource(value) {
    if (hasPathTraversal(value))
        return false;
    if (SKILL_RULE_SOURCES.includes(value))
        return true;
    if (!value.startsWith(SKILL_PLUGIN_SOURCE_PREFIX))
        return false;
    const plugin = value.slice(SKILL_PLUGIN_SOURCE_PREFIX.length);
    return plugin.length > 0 && !plugin.includes('*') && !plugin.includes('/');
}
/**
 * Skill marked-for-delete rule (KIR-4699).
 *
 * Exact, no globs — like packages, unlike extensions/MCP. A mark neutralizes
 * the file on every endpoint that has it, so `pdf*` would wipe unrelated
 * skills. Both fields are required: without `source` a rule spans the
 * project, personal and plugin skills that merely share a name.
 *
 * No Allow rule exists — `BLOCK_ONLY_POLICIES` refuses them.
 *
 * **A mark is not retroactive.** It takes effect on that file's next scan —
 * an edit, or the next agent start. The agent drops its file cache when the
 * detection key changes but deliberately does not re-walk already-scanned
 * files (`pkg/pluginhost/policyrefresher.go`, KIR-2754), so the UI must not
 * imply the skill is gone the moment the toggle flips.
 */
export const skillsManagementRuleSchema = z
    .object({
    name: z
        .string()
        .min(1, 'Skill name is required')
        .refine(name => !name.includes('*'), {
        message: 'Skill rules match an exact name; wildcards (*) are not supported',
    })
        .refine(name => !hasPathTraversal(name), {
        message: 'Skill name must not contain path traversal sequences',
    }),
    source: z.string().refine(isValidSkillSource, {
        message: 'Source must be "project", "user", or "plugin:<plugin-name>"',
    }),
    /**
     * KIR-4849: which project a `project`-source skill belongs to, as the
     * normalized git origin (`github.com/org/repo`). Without it one mark on
     * `(deploy, project)` quarantined every repo's `.claude/skills/deploy/`
     * across the org.
     *
     * Explicitly `null` rather than omitted for `user` / `plugin:` skills and
     * for project skills whose client reports no git origin — a required
     * nullable field keeps the rule self-describing and stops an omitted key
     * from reading as a wildcard.
     */
    project: z.string().min(1).nullable(),
})
    .strict();
/**
 * Skill names come from a directory basename and case varies by filesystem.
 * Every comparison — matcher, state column, row actions — goes through this,
 * or a mark on `pdf-processing` misses `PDF-Processing`.
 */
export function skillIdentityName(name) {
    return name.trim().toLowerCase();
}
/**
 * Rule-object fields a policy does not support, stripped before validation and
 * persistence (see `validateAndNormalizeRuleObject`). Stripping rather than
 * rejecting keeps a legacy rule editable — a GET/PUT round-trip drops the dead
 * field instead of returning a 400 the client cannot resolve.
 */
export const UNSUPPORTED_RULE_FIELDS_BY_POLICY = {
    [PolicyKey.McpServerSources]: ['version'],
    // KIR-4443: package identity is name + ecosystem. `publisher` has no meaning
    // for a flat registry name, and `version` is deliberately not part of the
    // rule (see packagesManagementRuleSchema). Stripped rather than rejected so a
    // caller reusing an extension-shaped payload still succeeds.
    [PolicyKey.PackagesManagement]: ['publisher', 'version'],
    // KIR-4699: identity is name + source; strip the extension/MCP-shaped
    // fields so a caller reusing one of those payloads still succeeds.
    [PolicyKey.SkillsManagement]: ['publisher', 'version', 'type'],
};
export const ruleObjectSchemasByPolicy = {
    [PolicyKey.ExtensionsManagement]: extensionsManagementRuleSchema,
    [PolicyKey.McpServerSources]: mcpServerSourcesRuleSchema,
    [PolicyKey.PackagesManagement]: packagesManagementRuleSchema,
    [PolicyKey.SkillsManagement]: skillsManagementRuleSchema,
};
// ---------------------------------------------------------------------------
// Generic ruleObject schema (used in create/update body for Fastify schema)
// ---------------------------------------------------------------------------
export const ruleObjectSchema = z
    .record(z.string(), z.unknown())
    .refine(obj => Object.values(obj).some(v => typeof v === 'string' && v.trim().length > 0), { message: 'At least one field in ruleObject must be a non-empty string' });
export const policyExceptionSchema = z.object({
    id: z.uuid(),
    organizationId: z.string().nullable(),
    userId: z.uuid().nullable(),
    // KIR-3800: set (with org/user null) for a security-group-scoped exception, so
    // a caller can tell group exceptions apart from org/user ones. Nullable +
    // additive — org/user exceptions serialize it as null.
    securityGroupId: z.string().nullable(),
    policyKey: policyKeySchema,
    action: policyExceptionActionSchema,
    ruleObject: z.record(z.string(), z.unknown()),
    createdBy: z.uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedBy: z.uuid().nullable(),
});
export const createPolicyExceptionSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
export const updatePolicyExceptionSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
// ---------------------------------------------------------------------------
// Add-from-alert (KIR-3558): one-click add of an alert's entity to a list
// ---------------------------------------------------------------------------
/**
 * The only policies the add-from-alert action can target — the Allow/Block
 * list policies. Single source of truth shared by the route param schema
 * (rejects any other key with a 400 before the handler runs) and the backend
 * handler's defensive guard, so the two can never drift.
 */
export const ADD_FROM_ALERT_SUPPORTED_POLICIES = [
    PolicyKey.ExtensionsManagement,
    PolicyKey.McpServerSources,
    PolicyKey.PackagesManagement,
];
/**
 * Route-param schema for the add-from-alert endpoints. Unlike `policyKeySchema`
 * (which accepts every policy key), this restricts to the Allow/Block list
 * policies so an unsupported-but-valid key is a client error (400 at schema
 * validation), never a server 500.
 */
export const fromAlertPolicyKeySchema = z.enum(ADD_FROM_ALERT_SUPPORTED_POLICIES);
/**
 * Request body for `POST .../exceptions/from-alert`. Identical shape to a
 * create, but the handler applies add-only prechecks (already-on-list /
 * covered-by-broad-rule) instead of surfacing duplicates as errors.
 */
export const addPolicyExceptionFromAlertSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: ruleObjectSchema,
});
/**
 * The existing rule that already covers the entity, returned on the two "stop"
 * outcomes so the UI can show exactly why nothing was added.
 */
export const coveringPolicyRuleSchema = z.object({
    action: policyExceptionActionSchema,
    ruleObject: z.record(z.string(), z.unknown()),
});
export var AddFromAlertStatus;
(function (AddFromAlertStatus) {
    AddFromAlertStatus["Added"] = "added";
    AddFromAlertStatus["AlreadyOnList"] = "already_on_list";
    AddFromAlertStatus["CoveredByBroadRule"] = "covered_by_broad_rule";
})(AddFromAlertStatus || (AddFromAlertStatus = {}));
/**
 * Result of an add-from-alert action, discriminated on `status`. All three are
 * successful (2xx) outcomes — the frontend branches on `status`, never on the
 * HTTP code, and reserves non-2xx for real failures.
 * - `added` (201): the exact entry was created.
 * - `already_on_list` (200): the exact entry already exists (no change).
 * - `covered_by_broad_rule` (200): a broader rule already covers it (no change).
 */
export const addFromAlertResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal(AddFromAlertStatus.Added),
        exception: policyExceptionSchema,
    }),
    z.object({
        status: z.literal(AddFromAlertStatus.AlreadyOnList),
        coveringRule: coveringPolicyRuleSchema,
    }),
    z.object({
        status: z.literal(AddFromAlertStatus.CoveredByBroadRule),
        coveringRule: coveringPolicyRuleSchema,
    }),
]);
export const listPolicyExceptionsQuerySchema = z.object({
    action: policyExceptionActionSchema.optional(),
});
export const policyExceptionListSchema = z.object({
    exceptions: z.array(policyExceptionSchema),
});
//# sourceMappingURL=policy-exceptions.js.map